import { FormikProvider, useFormik } from 'formik';
import { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';

// Components.
import { Button, ErrorLabel, Link, FormikInputField } from 'components';
import { Box } from '@mui/material';

// Core.
import { ButtonVariant, LinkVariant, useAppDispatch } from 'core';

// Hooks.
import { useAuthLogin } from 'hooks';

// Store.
import { sendCode } from 'store/onboarding/actions';
import { onboardingIsLoadingSelector } from 'store/onboarding/selectors';

// Styles.
import styles from './OnboardingVerifyEmailForm.module.scss';

// Validations.
import { validationSchema } from './validationSchema';

interface IOnboardingVerifyEmailForm {
  onSuccess?: VoidFunction;
}

export const OnboardingVerifyEmailForm = ({ onSuccess }: IOnboardingVerifyEmailForm): ReactElement => {
  const dispatch = useAppDispatch();
  const [error, setError] = useState<string>();
  const login = useAuthLogin();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
      passwordConfirmation: '',
    },
    onSubmit: async (values) => {
      try {
        await dispatch(sendCode(values));
        await onSuccess?.();
      } catch (error) {
        setError(error);
      }
    },
    validationSchema,
  });

  const { handleSubmit, isSubmitting } = formik;

  // Selectors
  const isLoading: boolean = useSelector(onboardingIsLoadingSelector);
  const isPending: boolean = isSubmitting || isLoading;

  return (
    <Box sx={{ width: '100%' }}>
      <FormikProvider value={formik}>
        <form name="Registration Form" onSubmit={handleSubmit} className={styles.form}>
          <fieldset className={styles.fieldSet}>
            <FormikInputField
              formikKey="email"
              label="Email"
              labelSubtext="The email that you use should be one that you check regularly and is not associated with a place of employment."
              required
              type="email"
            />
            <FormikInputField formikKey="password" label="Password" required type="password" />
            <FormikInputField formikKey="passwordConfirmation" label="Confirm Password" required type="password" />
          </fieldset>
          {error && <ErrorLabel error={error} name="email" />}
          <footer className={styles.footer}>
            <Button disabled={isPending} isSubmitting={isPending} type="submit" variant={ButtonVariant.Primary}>
              Create Account
            </Button>
            {error ? (
              <div>
                <Button className={styles.link} onClick={login} title="Log In" variant={ButtonVariant.Hyperlink}>
                  Log In
                </Button>
                <Link
                  className={styles.link}
                  title="Forgot My Password"
                  to="/forgot-password"
                  variant={LinkVariant.Hyperlink}
                >
                  Forgot My Password
                </Link>
              </div>
            ) : (
              <>
                <Box className={styles.login}>
                  Already have an account?{' '}
                  <Button className={styles.link} onClick={login} title="Log In" variant={ButtonVariant.Hyperlink}>
                    Log In
                  </Button>
                </Box>
              </>
            )}
          </footer>
        </form>
      </FormikProvider>
    </Box>
  );
};
